import CardsTable from 'components/CardsTable';
import { formatLanguages } from 'constants/languages';
import AddCards from './components/AddCards';
import Pagination from 'components/Pagination';
import './MyCardsCollection.css';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { Theme, useTheme } from '@mui/material';
import { CollectionList } from 'types/interfaces/collectionList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { MTGCard } from 'types/interfaces/mtgCard';
import { SortOrder } from 'types/interfaces/sort';
import { CardCondition } from 'types/interfaces/condition/condition';
import { AssetUrls } from 'config/assets';
import { useNavigate } from 'react-router';
import { useWindowSize } from 'hooks/useWindowSize';
import { Points } from 'components/Points/Points';
import { decimalToPoints } from 'utils/decimalUtils';
import { SetIcon } from 'components/SetIcon';
import { buildPrintingUrl } from 'utils/cardUrlUtils';
import { FoilCardImage } from 'components/FoilCardImage';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import ImportCardsModal from 'components/ImportCardsModal/ImportCardsModal';
import { BatchEditActionBar, HaveListBatchChanges, WantListBatchChanges } from 'components/BatchEditActionBar';
import { showBatchSuccess, showError } from 'utils/toastNotifications';
import { useHaveListAlertToggle } from 'hooks/useHaveListAlertToggle';
import { useSmsAlertsIntroGate } from 'hooks/useSmsAlertsIntroGate';
import SmsAlertsIntroModal from 'components/SmsAlertsIntroModal/SmsAlertsIntroModal';
import HaveListAlertBell from 'components/HaveListAlertBell/HaveListAlertBell';
import HaveListFoilButton from 'components/HaveListFoilButton/HaveListFoilButton';
import { haveListApi, useToggleFoilStatusMutation } from 'store/services/collectionList/haveList/haveListApi';
import { useDispatch } from 'react-redux';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';

/**
 * Card-detail href for one collection row: the printing the row DISPLAYS.
 *
 * This holds for every row type, including "Any Set" and multi-set want rows. Those
 * rows accept many printings at the API layer, but the row on screen renders one
 * specific printing's image and name, so that printing is what the row represents to
 * the user and linking anywhere else would be more surprising, not less. (Amends the
 * ticket's Decision 7, which said "the printing the row represents" — ruled 2026-08-03.)
 *
 * The href is keyed off row.card.scryfallID, the same field that sources the tile image
 * (see the image views below), so displayed and linked can never drift apart. When it is
 * absent the tile falls back to a generic card back rather than a printing, so there is
 * nothing honest to link to and the control is omitted.
 */
const collectionRowPrintingUrl = (row: CollectionList): string | null => {
  if (!row.card?.scryfallID) return null;
  return buildPrintingUrl(row.card);
};

/**
 * "View card details" control for the image views.
 *
 * A real anchor, so middle-click and cmd-click open a new tab and the browser shows a
 * hoverable target URL; plain click navigates in the same tab. stopPropagation (and
 * deliberately NOT preventDefault) keeps the tile's multi-select toggle from firing
 * while still letting the href drive navigation.
 *
 * Sits in the tile's top-right corner: top-left is occupied by the batch-select
 * checkbox (z-index 20) and the bottom row cannot fit a third 44px control inside a
 * 140px tile. Renders nothing when the tile shows a generic card back rather than a
 * specific printing.
 */
const CollectionViewCardLink = ({ row }: { row: CollectionList }) => {
  const href = collectionRowPrintingUrl(row);
  if (!href) return null;

  return (
    <a
      className="collection-card-item-view-btn"
      href={href}
      title="View card details"
      aria-label="View card details"
      onClick={(e) => e.stopPropagation()}
    >
      <img src={AssetUrls.search.arrowDiagonal} width={16} height={16} alt="" />
    </a>
  );
};

// Helper function to format condition display
const formatConditions = (acceptableConditions?: string[], minCondition?: CardCondition): string => {
  // If acceptableConditions is provided (new backend field), use it
  if (acceptableConditions && acceptableConditions.length > 0) {
    const conditionMap: Record<string, string> = {
      'NEAR_MINT': 'NM',
      'SLIGHTLY_PLAYED': 'LP',
      'MODERATELY_PLAYED': 'MP',
      'HEAVILY_PLAYED': 'HP',
      'DAMAGED': 'DMG',
    };
    
    const mapped = acceptableConditions.map(cond => conditionMap[cond] || cond);
    
    // Show "Any" when all 4 tradeable conditions (NM, LP, MP, HP) are selected
    if (mapped.length >= 4 && mapped.includes('NM') && mapped.includes('LP') && mapped.includes('MP') && mapped.includes('HP')) {
      return 'Any';
    }
    
    // Show the exact conditions selected
    return mapped.join(', ');
  }
  
  // Fallback to old minCondition logic
  if (!minCondition) return 'Any';
  
  const conditionMap: Record<CardCondition, string> = {
    [CardCondition.NEAR_MINT]: 'NM',
    [CardCondition.SLIGHTLY_PLAYED]: 'SP',
    [CardCondition.MODERATELY_PLAYED]: 'MP',
    [CardCondition.HEAVILY_PLAYED]: 'HP',
    [CardCondition.DAMAGED]: 'DMG',
  };

  const conditionOrder = [
    CardCondition.NEAR_MINT,
    CardCondition.SLIGHTLY_PLAYED,
    CardCondition.MODERATELY_PLAYED,
    CardCondition.HEAVILY_PLAYED,
    CardCondition.DAMAGED,
  ];

  // Find the index of the minimum condition
  const minIndex = conditionOrder.indexOf(minCondition);
  if (minIndex === -1) return 'Any';

  // Get all conditions from NM down to the minimum condition
  const acceptableConditionsOld = conditionOrder
    .slice(0, minIndex + 1)
    .map(cond => conditionMap[cond]);

  // If there are 4 or more conditions (NM, SP, MP, HP), show "Any" instead
  if (acceptableConditionsOld.length >= 4) {
    return 'Any';
  }

  return acceptableConditionsOld.join(', ');
};

// Helper function to format foil display
const formatFoilPreference = (acceptFoil?: boolean, acceptNonFoil?: boolean) => {
  if (acceptFoil && acceptNonFoil) {
    return 'Any';
  } else if (acceptFoil) {
    return (
      <img
        src={`${AssetUrls.base}/assets/add-cards/foil.png`}
        width={14.15}
        height={14.21}
        alt="foil-icon"
      />
    );
  }
  return '-'; // Non-foil only, show dash for cleaner display
};

// Helper function to calculate per-card point value based on foil status
const calculatePointValue = (row: CollectionList, isHaveList: boolean): number | null => {
  // Use backend-provided displayPrice if available (already in points)
  // Backend handles etched fallback: usdFoil -> usdEtched for foil items
  if (row.displayPrice != null) {
    return row.displayPrice;
  }

  // Fallback for backwards compatibility
  // Guard: Check if card exists
  if (!row.card) return null;

  // Extract price data (backend returns array)
  const priceData = Array.isArray(row.card.price) && row.card.price.length > 0
    ? row.card.price[0]
    : (!Array.isArray(row.card.price) ? row.card.price : null);

  if (!priceData) return null;

  // HAVE LIST: Check actual foil status of the owned card
  if (isHaveList && row.foil) {
    return priceData.usdFoil ? decimalToPoints(priceData.usdFoil) : null;
  }

  // DEFAULT: Non-foil price for:
  // - Have List non-foil cards
  // - Want List cards (backend already selected appropriate printing)
  return priceData.usd ? decimalToPoints(priceData.usd) : null;
};

const getCollectionTableCells = (
  theme: Theme,
  rows: CollectionList[],
  quantityChange: (identifier: number | string, change: number) => void,
  onCardDelete: (identifier: number | string) => void,
  isWantList: boolean = false,
  isHaveList: boolean = false,
  onCardEdit?: (row: CollectionList) => void,
  onCardHover?: (card: MTGCard | undefined) => void,
  isMobile: boolean = false,
  onCardNameHover?: (e: React.MouseEvent, scryfallId: string, cardName: string) => void,
  onCardNameLeave?: () => void,
  onTradeStatusToggle?: (haveListRecordId: number, forTrade: boolean) => void,
  selectedIds?: Set<number>,
  onToggleSelect?: (id: number) => void,
  onCheckboxClick?: (index: number, e?: React.MouseEvent) => void,
  alertsEnabled: boolean = false,
  alertsPhoneVerified: boolean = false,
  onAlertToggle?: (haveListRecordId: number, enable: boolean) => void,
  onFoilToggle?: (haveListRecordId: number, newFoil: boolean) => void
): CardsTableCellsData => {

  const headCells: any[] = [];

  // Persistent batch-select checkbox column
  headCells.push({ key: 'batch-select', label: '', columnWidth: 14, noSort: true });

  headCells.push(
    { key: 'quantity', label: 'Quantity', columnWidth: isMobile ? 50 : 60, noSort: true },
    { key: 'card_name', label: 'Name' },
    { key: 'setName', label: 'Set', noSort: true },
  );

  // Trade status column for have list (non-mobile only)
  if (isHaveList && !isMobile) {
    headCells.push({ key: 'forTrade', label: 'Trade', columnWidth: 50, noSort: true } as any);
  }

  // SMS alerts column for have list (non-mobile)
  if (isHaveList && !isMobile && alertsEnabled) {
    headCells.push({
      key: 'alert_enabled',
      label: 'Alert',
      columnWidth: 90,
      headerTooltip: 'Get a text message when someone wants a card you have.',
      headerLearnMoreUrl: 'https://trademagic.gg/articles/sms-trade-alerts',
    } as any);
  }

  // Only include these columns on non-mobile
  if (!isMobile) {
    headCells.push(
      { key: 'isFoil', label: 'Foil', columnWidth: 40, noSort: true } as any,
      { key: 'condition', label: 'Condition', columnWidth: 70, noSort: true } as any,
      { key: 'lang', label: 'Language', columnWidth: 50, noSort: true } as any,
      { key: 'reference_price', label: 'Value', columnWidth: 70 } as any
    );
  }

  // Always include actions column at the end.
  // 112px = three 32px .row-action-btn controls (view / edit / remove) + two 8px gaps.
  // CardsTable renders tableLayout:'fixed' and CardsTableCell clips at maxWidth, so an
  // under-sized column silently cuts the leftmost control off.
  headCells.push({ key: 'actions', label: '', noSort: true, columnWidth: 112 } as any);
  
  return {
    headCells,
  bodyRows: rows.map((row, rowIndex) => {
    const components: (React.ReactNode | string)[] = [];

    // Persistent per-row checkbox with shift-click range support
    components.push(
      <div className="batch-select-cell" onClick={(e) => { e.stopPropagation(); onCheckboxClick?.(rowIndex, e); }}>
        <input
          type="checkbox"
          className="batch-checkbox"
          checked={selectedIds?.has(row.id) || false}
          onChange={() => {}}
        />
      </div>
    );

    components.push(
      <div className="collection-card-quantity-container">
        <div
          className={`collection-card-quantity-icon-wrapper ${row.quantity > 1 ? '' : 'disabled'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (row.quantity > 1) {
              console.log('Minus button clicked for row:', { 
                rowId: row.id,
                oracleId: row.oracleId, 
                cardId: row.card?.id,
                identifier: row.id,
                quantity: row.quantity 
              });
              // For want list, use the unique row.id instead of oracleId
              quantityChange(row.id, -1);
            }
          }}
        >
          <FontAwesomeIcon
            className="collection-card-quantity-icon"
            icon={'minus'}
            color={theme.colors.darkerGrey}
          />
        </div>
        <div className="collection-card-quantity">{row.quantity}</div>
        <div
          className="collection-card-quantity-icon-wrapper"
          onClick={(e) => {
            e.stopPropagation();
            console.log('Plus button clicked for row:', { 
              rowId: row.id,
              oracleId: row.oracleId, 
              cardId: row.card?.id,
              identifier: row.id,
              quantity: row.quantity 
            });
            // For want list, use the unique row.id instead of oracleId
            quantityChange(row.id, 1);
          }}
        >
          <FontAwesomeIcon
            className="collection-card-quantity-icon"
            icon={'plus'}
            color={theme.colors.darkerGrey}
          />
        </div>
      </div>,
      <div
        className="collection-card-name"
        onMouseEnter={(e) => row.card?.scryfallID && onCardNameHover?.(e, row.card.scryfallID, row.cardName || row.card?.name || '')}
        onMouseLeave={onCardNameLeave}
      >
        {row.card?.scryfallID && (
          <img
            src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
            className="collection-card-name-img"
            alt={`table-row-image-${row.cardName || row.card?.name}`}
          />
        )}
        <span className="collection-card-name-text">
          {row.cardName || row.card?.name}
        </span>
      </div>,
      // Set column - simple JSX like Name column
      <div className="collection-card-set">
        <SetIcon setIconUrl={row.card?.setIconUrl} setName={row.card?.setName} />
        {/* Display set name with collector number */}
        <span className="collection-card-set-text">
          {(() => {
            if (isHaveList) {
              // Have List: Show single set name with collector number
              if (row.card?.setName) {
                return <>{row.card.setName}{row.card.collectorNumber && <span className="collector-number"> #{row.card.collectorNumber}</span>}</>;
              }
              return '';
            }
            // Want List: Check if this is a want list entry with "Any Set" (empty acceptableSets)
            if (isWantList && (!row.acceptableSets || row.acceptableSets.length === 0)) {
              return 'Any';
            }
            // If there are specific acceptable sets, show them
            if (row.acceptableSets && row.acceptableSets.length > 0) {
              if (row.acceptableSets.length === 1 && row.card?.collectorNumber) {
                return <>{row.acceptableSets[0]}<span className="collector-number"> #{row.card.collectorNumber}</span></>;
              }
              return row.acceptableSets.join(', ');
            }
            // For when there's a specific card, show the set name
            if (row.card?.setName) {
              return <>{row.card.setName}{row.card.collectorNumber && <span className="collector-number"> #{row.card.collectorNumber}</span>}</>;
            }
            return '';
          })()}
        </span>
      </div>,
    );

    // Trade status toggle for have list (non-mobile only)
    if (isHaveList && !isMobile) {
      const isForTrade = row.forTrade !== false;
      components.push(
        <div className="collection-card-trade-toggle" onClick={(e) => e.stopPropagation()}>
          <button
            className={`trade-status-toggle ${isForTrade ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onTradeStatusToggle?.(row.id, !isForTrade);
            }}
            title={isForTrade ? 'For Trade' : 'Not for Trade'}
          >
            <FontAwesomeIcon icon={isForTrade ? 'right-left' : 'lock'} fontSize={12} />
          </button>
        </div>
      );
    }

    // SMS alert toggle for have list (non-mobile)
    if (isHaveList && !isMobile && alertsEnabled) {
      components.push(
        <HaveListAlertBell
          enabled={!!row.alertEnabled}
          phoneVerified={alertsPhoneVerified}
          cardName={row.card?.name ?? row.cardName ?? 'card'}
          onToggle={(enable) => onAlertToggle?.(row.id, enable)}
        />
      );
    }

    // Only include these columns on non-mobile
    if (!isMobile) {
      // Foil availability comes from MTGCard.foil (Scryfall finish flag) — the same signal the
      // edit modal trusts. Price-based detection (usdFoil != null) is unreliable here because
      // /have-list/me returns one price record per row whose usdFoil is often null even when
      // foil is offered. Owning a foil row is also sufficient evidence foil is available.
      const foilPrintingAvailable = row.card?.foil !== false || !!row.foil;
      components.push(
        // Foil column - wrap in div if it's a string
        isHaveList ? (
          <HaveListFoilButton
            isFoil={!!row.foil}
            foilAvailable={foilPrintingAvailable}
            cardName={row.card?.name ?? row.cardName ?? 'card'}
            onToggle={(newFoil) => onFoilToggle?.(row.id, newFoil)}
          />
        ) : (
          <div>{formatFoilPreference(row.acceptFoil, row.acceptNonFoil)}</div>
        ),
        // Condition column
        <div>
          {isHaveList
            ? (row.condition ? (() => {
                const conditionMap: Record<string, string> = {
                  'NEAR_MINT': 'NM',
                  'SLIGHTLY_PLAYED': 'LP',
                  'MODERATELY_PLAYED': 'MP',
                  'HEAVILY_PLAYED': 'HP',
                  'DAMAGED': 'DMG',
                };
                return conditionMap[row.condition] || row.condition;
              })() : '')
            : formatConditions(row.acceptableConditions, row.minCondition)}
        </div>,
        // Language column
        <div>
          {isHaveList
            ? (row.card?.lang?.toUpperCase() || '')
            : (isWantList ? formatLanguages(row.acceptableLanguages) : (row.card?.lang?.toUpperCase() || ''))}
        </div>,
        // Value column
        <div>
          <Points
            points={calculatePointValue(row, isHaveList) || 0}
            type="small"
          />
        </div>
      );
    }

    // Always include actions column at the end
    const viewCardHref = collectionRowPrintingUrl(row);
    components.push(
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {/* View card details — a real anchor so middle-click and cmd-click open a new tab.
            stopPropagation (never preventDefault) keeps the row's update-versions/edit
            action from firing while letting the href drive navigation. Omitted when the
            row has no single printing to point at (oracle-level "Any Set" want rows). */}
        {viewCardHref && (
          <a
            className="row-action-btn"
            href={viewCardHref}
            title="View card details"
            aria-label="View card details"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={AssetUrls.search.arrowDiagonal}
              width={14}
              height={14}
              alt=""
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </a>
        )}
        {(isWantList || isHaveList) && onCardEdit && (
          <button
            type="button"
            className="row-action-btn"
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              onCardEdit(row);
            }}
          >
            <img
              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/edit.svg"
              width={14}
              height={14}
              alt="Edit"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </button>
        )}
        <button
          type="button"
          className="row-action-btn"
          title="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onCardDelete(row.id);
          }}
        >
          <FontAwesomeIcon icon={'times'} fontSize={14} />
        </button>
      </div>
    );
    
    return {
      key: row.id.toString(),
      card: row.card || ({} as MTGCard),
      onMouseEnter: onCardHover ? () => onCardHover(row.card || undefined) : undefined,
      onMouseLeave: onCardHover ? () => onCardHover(undefined) : undefined,
      components
    };
  }),
  };
};

interface Props {
  isLoading: boolean;
  addCardsPlaceholder: string;
  addCardsTitle?: string;
  addCardsSubtitle?: string;
  data: CollectionList[];
  order?: SortOrder;
  orderBy?: string;
  onCardAdd: (identifier: number | string, cardData?: any, searchMode?: 'any' | 'specific') => void;
  onHeaderSortPress: (key: string, direction?: SortOrder) => void;
  cardQuantityChange: (identifier: number | string, change: number) => void;
  onCardDelete: (identifier: number | string) => void;
  onCardEdit?: (row: CollectionList) => void; // Callback for edit button
  onEndReached?: () => void; // Made optional for pagination mode
  useOracleId?: boolean; // Flag to determine whether to use oracleId
  isWantList?: boolean; // Flag to enable inline editing for want list
  isHaveList?: boolean; // Flag to enable have list specific behavior
  onDeleteAll?: () => void; // Callback for delete all functionality
  viewMode?: 'list' | 'comfortable' | 'compact';
  onViewModeChange?: (mode: 'list' | 'comfortable' | 'compact') => void;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;      // Total individual cards (sum of quantities)
  totalRecords?: number;    // Total unique records/items
  onPageChange?: (page: number) => void;
  // Search props
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  isSearchOpen?: boolean;
  onSearchToggle?: () => void;
  filteredCount?: number;
  // Trade status props (have list only)
  onTradeStatusToggle?: (haveListRecordId: number, forTrade: boolean) => void;
  tradeFilter?: 'all' | 'forTrade' | 'notForTrade';
  onTradeFilterChange?: (filter: 'all' | 'forTrade' | 'notForTrade') => void;
  // Batch edit props
  onBatchUpdateHaveList?: (ids: number[], changes: import('components/BatchEditActionBar').HaveListBatchChanges) => Promise<{ successCount: number; totalCount: number }>;
  onBatchUpdateWantList?: (ids: number[], changes: import('components/BatchEditActionBar').WantListBatchChanges) => Promise<{ successCount: number; totalCount: number }>;
  onExportCsv?: () => void;
  isExporting?: boolean;
}

export const MyCardsCollection = ({
  isLoading,
  addCardsPlaceholder,
  addCardsTitle,
  addCardsSubtitle,
  data,
  order,
  orderBy,
  onCardAdd,
  onHeaderSortPress,
  cardQuantityChange,
  onCardDelete,
  onCardEdit,
  onEndReached,
  useOracleId = false,
  isWantList = false,
  isHaveList = false,
  onDeleteAll,
  viewMode = 'list',
  onViewModeChange,
  currentPage,
  totalPages,
  totalItems,
  totalRecords,
  onPageChange,
  searchTerm,
  onSearchChange,
  isSearchOpen,
  onSearchToggle,
  filteredCount,
  onTradeStatusToggle,
  tradeFilter,
  onTradeFilterChange,
  onBatchUpdateHaveList,
  onBatchUpdateWantList,
  onExportCsv,
  isExporting,
}: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize(480);
  const alerts = useHaveListAlertToggle();
  const smsIntroGate = useSmsAlertsIntroGate();
  const [toggleFoilStatus] = useToggleFoilStatusMutation();
  const dispatch = useDispatch();
  const inFlightFoilRef = useRef<Set<number>>(new Set());

  const handleFoilToggle = useCallback(
    async (haveListRecordId: number, foil: boolean) => {
      if (inFlightFoilRef.current.has(haveListRecordId)) return;
      inFlightFoilRef.current.add(haveListRecordId);
      try {
        await toggleFoilStatus({ haveListRecordId, foil }).unwrap();
      } catch (err: any) {
        if (err?.status === 404) {
          // Row was merged or deleted server-side (the backend's foil toggle can collapse
          // (cardId, condition, foil) duplicates into a single row). Refetch to converge —
          // the user's intent was already honored, so don't surface a toast.
          dispatch(haveListApi.util.invalidateTags(['HaveList']));
        } else {
          showError('Failed to update foil status. Please try again.');
        }
      } finally {
        inFlightFoilRef.current.delete(haveListRecordId);
      }
    },
    [toggleFoilStatus, dispatch]
  );

  // Per-row alert bell handler. Enabling alerts is gated behind the first-time
  // SMS Alerts intro modal — the real toggle mutation only fires once the user
  // gives consent, so the bell never flickers on then off. Disabling is never gated.
  const { toggleOne } = alerts;
  const { guardEnable: guardSmsIntro } = smsIntroGate;
  const handleAlertToggle = useCallback(
    (recordId: number, enable: boolean) => {
      if (enable) {
        guardSmsIntro(() => toggleOne(recordId, true));
      } else {
        toggleOne(recordId, false);
      }
    },
    [guardSmsIntro, toggleOne]
  );

  // Batch select state — selection mode is derived from having selections
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBatchApplying, setIsBatchApplying] = useState(false);
  const lastCheckedIndex = useRef<number | null>(null);

  const handleCheckboxClick = useCallback((index: number, e?: React.MouseEvent) => {
    const id = data[index]?.id;
    if (id == null) return;

    if (e?.shiftKey && lastCheckedIndex.current != null && lastCheckedIndex.current !== index) {
      // Shift-click: select/deselect range based on anchor row's current state
      const anchor = lastCheckedIndex.current;
      const start = Math.min(anchor, index);
      const end = Math.max(anchor, index);
      const anchorId = data[anchor]?.id;
      const shouldSelect = anchorId != null && selectedIds.has(anchorId);

      setSelectedIds(prev => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          const rowId = data[i]?.id;
          if (rowId != null) {
            if (shouldSelect) next.add(rowId);
            else next.delete(rowId);
          }
        }
        return next;
      });
    } else {
      // Normal click: toggle single row and set anchor
      lastCheckedIndex.current = index;
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  }, [data, selectedIds]);

  // Legacy toggle by id (used by grid views)
  const toggleSelection = useCallback((id: number) => {
    const index = data.findIndex(r => r.id === id);
    if (index !== -1) lastCheckedIndex.current = index;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [data]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (prev.size === data.length) return new Set(); // Deselect all
      return new Set(data.map(r => r.id)); // Select all visible
    });
  }, [data]);

  // Clear selection on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedIds.size > 0) {
        setSelectedIds(new Set());
        lastCheckedIndex.current = null;
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedIds.size]);

  // Clear selection on page change
  useEffect(() => {
    setSelectedIds(new Set());
    lastCheckedIndex.current = null;
  }, [currentPage]);

  // Clear selection on view mode change
  useEffect(() => {
    if (selectedIds.size > 0) {
      setSelectedIds(new Set());
    }
    lastCheckedIndex.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Reset anchor on sort/filter changes (row indices shift)
  useEffect(() => {
    lastCheckedIndex.current = null;
  }, [orderBy, order, searchTerm]);

  // State for card image tooltip on hover
  const [hoveredCard, setHoveredCard] = useState<{
    scryfallId: string;
    cardName: string;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isMobileDevice, setIsMobileDevice] = useState(window.innerWidth <= 768);
  const [showEmptyStateImport, setShowEmptyStateImport] = useState(false);

  // Update mobile detection on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hover handlers for card name tooltip
  const handleCardNameHover = useCallback((e: React.MouseEvent, scryfallId: string, cardName: string) => {
    if (isMobileDevice) return;
    setTooltipPosition({ x: e.clientX, y: e.clientY });
    setHoveredCard({ scryfallId, cardName });
  }, [isMobileDevice]);

  const handleCardNameLeave = useCallback(() => {
    setHoveredCard(null);
  }, []);

  const onTableCardDelete = useCallback(
    (identifier: number | string) => {
      onCardDelete(identifier);
    },
    [onCardDelete]
  );

  // Handle edit for Want List - navigate to ViewCardPrintings in edit mode
  const handleWantListEdit = useCallback((row: CollectionList) => {
    if (row.cardName) {
      const params = new URLSearchParams({ editWantId: row.id.toString() });
      // Handle both scryfallId (backend) and scryfallID (TypeScript interface) casings
      const scryfallId = (row.card as any)?.scryfallId || row.card?.scryfallID;
      if (scryfallId) {
        params.append('cardId', scryfallId);
      }
      navigate(`/view-card-printings/${encodeURIComponent(row.cardName)}?${params.toString()}`);
    }
  }, [navigate]);

  // Use the appropriate edit handler based on mode
  const effectiveOnCardEdit = onCardEdit || (isWantList ? handleWantListEdit : undefined);

  const tableCellsData = useMemo(
    () =>
      getCollectionTableCells(
        theme,
        data,
        cardQuantityChange,
        onTableCardDelete,
        isWantList,
        isHaveList,
        effectiveOnCardEdit,
        undefined,
        isMobile,
        handleCardNameHover,
        handleCardNameLeave,
        onTradeStatusToggle,
        selectedIds,
        toggleSelection,
        handleCheckboxClick,
        alerts.canUseFeature,
        alerts.phoneVerified,
        handleAlertToggle,
        isHaveList ? handleFoilToggle : undefined
      ),
    [data, cardQuantityChange, onTableCardDelete, theme, isWantList, isHaveList, effectiveOnCardEdit, isMobile, handleCardNameHover, handleCardNameLeave, onTradeStatusToggle, selectedIds, toggleSelection, handleCheckboxClick, alerts.canUseFeature, alerts.phoneVerified, handleAlertToggle, handleFoilToggle]
  );

  const onCardSelect = (card: MTGCard) => {
    // In want list mode, clicking enters edit mode
    if (isWantList) {
      // Find the row that matches this card to get its ID
      const row = data.find(r => r.card?.id === card.id || r.oracleId === (card as any).oracleId);
      if (row && row.cardName) {
        const params = new URLSearchParams({ editWantId: row.id.toString() });
        // Handle both scryfallId (backend) and scryfallID (TypeScript interface) casings
        const scryfallId = (row.card as any)?.scryfallId || row.card?.scryfallID;
        if (scryfallId) {
          params.append('cardId', scryfallId);
        }
        navigate(`/view-card-printings/${encodeURIComponent(row.cardName)}?${params.toString()}`);
      }
    }
    // In have list mode, clicking opens edit modal
    if (isHaveList && effectiveOnCardEdit) {
      const row = data.find(r => r.card?.id === card.id);
      if (row) {
        effectiveOnCardEdit(row);
      }
    }
  };

  // Render pagination controls
  const renderPagination = () => {
    if (!onPageChange || !currentPage || !totalPages) return null;
    if (totalPages <= 1) return null; // Don't show pagination if only one page

    return (
      <div className="collection-pagination">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
        {(totalRecords || totalItems) && (
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
            <span className="pagination-total">
              {' '}(
              {totalRecords && totalRecords > 0 && `${totalRecords} items`}
              {totalRecords && totalItems && totalItems > 0 && ', '}
              {totalItems && totalItems > 0 && `${totalItems} total cards`}
              )
            </span>
          </div>
        )}
      </div>
    );
  };

  // Render card grid for image views
  const renderCardGrid = () => {
    if (viewMode === 'comfortable') {
      // Comfortable mode: Show individual card images for each quantity
      return (
        <div className="collection-card-grid">
          {data.flatMap((row, rowIndex) => {
            return Array.from({ length: row.quantity }, (_, index) => {
              const cardKey = `${row.id}-${index}`;
              const scryfallId = row.card?.scryfallID;
              const imageUrl = scryfallId
                ? AssetUrls.cards.getCardImage(scryfallId, 'normal')
                : AssetUrls.cards.defaultBack;

              return (
                <div
                  key={cardKey}
                  className="collection-card-item"
                  onClick={() => toggleSelection(row.id)}
                >
                  {index === 0 && (
                    <div className={`batch-select-grid-overlay ${selectedIds.has(row.id) ? 'checked' : ''}`} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="batch-checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelection(row.id)}
                      />
                    </div>
                  )}
                  <FoilCardImage
                    className="collection-card-item-image"
                    src={imageUrl}
                    alt={row.cardName || row.card?.name || 'Card'}
                    loading="lazy"
                    foil={isHaveList ? row.foil : (row.acceptFoil && !row.acceptNonFoil)}
                    borderRadius="8px"
                    onError={(e) => {
                      const img = e.currentTarget;
                      const currentSrc = img.src;

                      // First try backend-provided fallback URLs if available
                      const fallbacks = (row.card as any)?.normal_fallbacks;
                      if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                        img.dataset.fallbackAttempted = 'true';
                        img.src = fallbacks[0];
                        return;
                      }

                      // Otherwise, try different image formats using scryfallId
                      if (scryfallId && !img.dataset.formatFallbackComplete) {
                        // Determine current format from URL
                        if (currentSrc.includes('/normal/')) {
                          // Normal format failed, try small
                          img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                          return;
                        } else if (currentSrc.includes('/small/')) {
                          // Small format failed, try large
                          img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                          return;
                        } else if (currentSrc.includes('/large/')) {
                          // Large format failed, mark as complete and use placeholder
                          img.dataset.formatFallbackComplete = 'true';
                        }
                      }

                      // If all fallbacks failed, use default card back
                      img.src = AssetUrls.cards.defaultBack;
                    }}
                  />
                  {!scryfallId && (
                    <div className="card-name-overlay">
                      {row.cardName || row.card?.name || 'Unknown Card'}
                    </div>
                  )}
                  <div className="card-item-points">
                    <Points
                      points={calculatePointValue(row, isHaveList) || 0}
                      type="small"
                    />
                  </div>
                  {isHaveList && row.forTrade === false && (
                    <div className="card-not-for-trade-badge" title="Not for Trade">
                      <FontAwesomeIcon icon="lock" fontSize={10} />
                    </div>
                  )}
                  <div className="collection-card-item-hover-actions">
                    <CollectionViewCardLink row={row} />
                    {(isWantList || isHaveList) && effectiveOnCardEdit && (
                      <button
                        className="collection-card-item-edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          effectiveOnCardEdit(row);
                        }}
                        aria-label="Edit card"
                      >
                        <img
                          src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/edit.svg"
                          width={16}
                          height={16}
                          alt="edit"
                        />
                      </button>
                    )}
                    <button
                      className="collection-card-item-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCardDelete(row.id);
                      }}
                      aria-label="Delete card"
                    >
                      <FontAwesomeIcon icon={'times'} />
                    </button>
                  </div>
                </div>
              );
            });
          })}
        </div>
      );
    } else if (viewMode === 'compact') {
      // Compact mode: Show one card image with quantity badge
      return (
        <div className="collection-card-grid">
          {data.map((row, rowIndex) => {
            const scryfallId = row.card?.scryfallID;
            const imageUrl = scryfallId
              ? AssetUrls.cards.getCardImage(scryfallId, 'normal')
              : AssetUrls.cards.defaultBack;

            return (
              <div
                key={row.id}
                className="collection-card-item"
                onClick={() => toggleSelection(row.id)}
              >
                <div className={`batch-select-grid-overlay ${selectedIds.has(row.id) ? 'checked' : ''}`} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="batch-checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleSelection(row.id)}
                  />
                </div>
                <FoilCardImage
                  className="collection-card-item-image"
                  src={imageUrl}
                  alt={row.cardName || row.card?.name || 'Card'}
                  loading="lazy"
                  foil={isHaveList ? row.foil : (row.acceptFoil && !row.acceptNonFoil)}
                  borderRadius="8px"
                  onError={(e) => {
                    const img = e.currentTarget;
                    const currentSrc = img.src;

                    // First try backend-provided fallback URLs if available
                    const fallbacks = (row.card as any)?.normal_fallbacks;
                    if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
                      img.dataset.fallbackAttempted = 'true';
                      img.src = fallbacks[0];
                      return;
                    }

                    // Otherwise, try different image formats using scryfallId
                    if (scryfallId && !img.dataset.formatFallbackComplete) {
                      // Determine current format from URL
                      if (currentSrc.includes('/normal/')) {
                        // Normal format failed, try small
                        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                        return;
                      } else if (currentSrc.includes('/small/')) {
                        // Small format failed, try large
                        img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                        return;
                      } else if (currentSrc.includes('/large/')) {
                        // Large format failed, mark as complete and use placeholder
                        img.dataset.formatFallbackComplete = 'true';
                      }
                    }

                    // If all fallbacks failed, use default card back
                    img.src = AssetUrls.cards.defaultBack;
                  }}
                />
                {!scryfallId && (
                  <div className="card-name-overlay">
                    {row.cardName || row.card?.name || 'Unknown Card'}
                  </div>
                )}
                {row.quantity > 1 && (
                  <div className="card-quantity-badge">
                    {row.quantity}
                  </div>
                )}
                <div className="card-item-points">
                  <Points
                    points={calculatePointValue(row, isHaveList) || 0}
                    type="small"
                  />
                </div>
                {isHaveList && row.forTrade === false && (
                  <div className="card-not-for-trade-badge" title="Not for Trade">
                    <FontAwesomeIcon icon="lock" fontSize={10} />
                  </div>
                )}
                <div className="collection-card-item-hover-actions">
                  <CollectionViewCardLink row={row} />
                  {(isWantList || isHaveList) && effectiveOnCardEdit && (
                    <button
                      className="collection-card-item-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        effectiveOnCardEdit(row);
                      }}
                      aria-label="Edit card"
                    >
                      <img
                        src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/edit.svg"
                        width={16}
                        height={16}
                        alt="edit"
                      />
                    </button>
                  )}
                  <button
                    className="collection-card-item-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCardDelete(row.id);
                    }}
                    aria-label="Delete card"
                  >
                    <FontAwesomeIcon icon={'times'} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="my-cards-collection">
      <div className="collection-container">
        <div className="collection-add-cards-container">
          <AddCards
            placeholder={addCardsPlaceholder}
            title={addCardsTitle}
            subtitle={addCardsSubtitle}
            onCardAdd={onCardAdd}
            useOracleId={useOracleId}
            isHaveList={isHaveList}
            isWantList={isWantList}
            onDeleteAll={onDeleteAll}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            isSearchOpen={isSearchOpen}
            onSearchToggle={onSearchToggle}
            filteredCount={filteredCount}
            orderBy={orderBy}
            order={order}
            onSortChange={onHeaderSortPress}
            tradeFilter={tradeFilter}
            onTradeFilterChange={onTradeFilterChange}
            onExportCsv={onExportCsv}
            isExporting={isExporting}
          />
        </div>
        <div className="collection-cards-container">
          {isLoading && onPageChange ? (
            <div className="collection-loading-overlay">
              <div className="loading-spinner" />
            </div>
          ) : (
            <>
              {viewMode === 'list' ? (
                <CardsTable
                  isLoading={isLoading}
                  cellsData={tableCellsData}
                  orderBy={orderBy}
                  order={order}
                  rowDividerType="color-mix"
                  onHeaderSortPress={onHeaderSortPress}
                  onCardSelect={onCardSelect}
                  onEndReached={onEndReached}
                  onImportClick={() => setShowEmptyStateImport(true)}
                  selectedKeys={new Set(Array.from(selectedIds).map(String))}
                  onToggleSelect={(key) => toggleSelection(Number(key))}
                  onSelectAll={toggleSelectAll}
                  isAllSelected={data.length > 0 && selectedIds.size === data.length}
                  isIndeterminate={selectedIds.size > 0 && selectedIds.size < data.length}
                />
              ) : (
                renderCardGrid()
              )}
              {renderPagination()}
            </>
          )}
        </div>
      </div>
      {showEmptyStateImport && (
        <ImportCardsModal
          isOpen={showEmptyStateImport}
          onClose={() => setShowEmptyStateImport(false)}
          onCardAdd={onCardAdd}
          useOracleId={useOracleId}
          isHaveList={isHaveList}
        />
      )}
      {/* Card image tooltip for list view hover */}
      {hoveredCard && (
        <CardImageTooltip
          scryfallId={hoveredCard.scryfallId}
          cardName={hoveredCard.cardName}
          position={tooltipPosition}
          onMouseEnter={() => {}}
          onMouseLeave={handleCardNameLeave}
        />
      )}
      {/* Batch edit action bar */}
      {selectedIds.size > 0 && (
        <BatchEditActionBar
          selectedCount={selectedIds.size}
          isHaveList={isHaveList}
          isWantList={isWantList}
          selectedRows={data.filter(r => selectedIds.has(r.id))}
          isApplying={isBatchApplying}
          onApplyHaveList={onBatchUpdateHaveList ? async (changes: HaveListBatchChanges) => {
            const applyBatch = async () => {
              // Enforce phone-verified gate before sending alert-enable changes to the backend.
              // The backend rejects the whole batch with 403 if not verified, so catch it client-side first.
              if (changes.alertEnabled === true && !alerts.phoneVerified) {
                await alerts.toggleMany(Array.from(selectedIds), true);
                return;
              }
              setIsBatchApplying(true);
              try {
                const result = await onBatchUpdateHaveList(Array.from(selectedIds), changes);
                showBatchSuccess(result.successCount, result.totalCount, 'cards');
                setSelectedIds(new Set());
              } catch (err) {
                showError('Failed to apply batch changes');
              } finally {
                setIsBatchApplying(false);
              }
            };
            // Bulk-enabling alerts is gated behind the first-time SMS Alerts intro modal.
            if (changes.alertEnabled === true) {
              smsIntroGate.guardEnable(applyBatch);
            } else {
              await applyBatch();
            }
          } : undefined}
          onApplyWantList={onBatchUpdateWantList ? async (changes: WantListBatchChanges) => {
            setIsBatchApplying(true);
            try {
              const result = await onBatchUpdateWantList(Array.from(selectedIds), changes);
              showBatchSuccess(result.successCount, result.totalCount, 'cards');
              setSelectedIds(new Set());
            } catch (err) {
              showError('Failed to apply batch changes');
            } finally {
              setIsBatchApplying(false);
            }
          } : undefined}
          onCancel={() => {
            setSelectedIds(new Set());
          }}
        />
      )}
      {alerts.canUseFeature && (
        <MobileVerificationModal
          isOpen={alerts.verifyPromptProps.open}
          onClose={alerts.verifyPromptProps.onClose}
          currentPhone={null}
          onVerificationSuccess={alerts.verifyPromptProps.onVerificationSuccess}
        />
      )}
      <SmsAlertsIntroModal
        isOpen={smsIntroGate.isModalOpen}
        onConfirm={smsIntroGate.handleConfirm}
        onDismiss={smsIntroGate.handleDismiss}
      />
    </div>
  );
};
